import {
  Lesson,
  Scene,
  LessonSceneDefaults,
  BatchImportAnalyzeResult,
  BatchImportConfirmPayload,
} from '../types/index.js';
import { auth } from './firebase.js';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

export class ApiError extends Error {
  statusCode: number;
  constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;
    this.name = 'ApiError';
  }
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  let token = localStorage.getItem('edurender_token');

  // If Firebase currentUser exists, obtain fresh token seamlessly
  if (auth.currentUser) {
    try {
      token = await auth.currentUser.getIdToken();
      localStorage.setItem('edurender_token', token);
    } catch {
      // fallback to cached token
    }
  }

  if (!token) {
    token = 'dev-token-customer-admin';
  }

  const headers: Record<string, string> = {
    ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${API_BASE}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    // If we have an active user, try a forced token refresh and retry once
    if (auth.currentUser) {
      try {
        const freshToken = await auth.currentUser.getIdToken(true);
        localStorage.setItem('edurender_token', freshToken);
        const retryHeaders = { ...headers, Authorization: `Bearer ${freshToken}` };
        const retryRes = await fetch(url, { ...options, headers: retryHeaders });
        if (retryRes.ok) {
          return retryRes.json();
        }
      } catch {
        // Fall through
      }
    }

    if (window.location.pathname !== '/login') {
      localStorage.removeItem('edurender_token');
    }
    throw new ApiError('Sessão expirada. Faça login novamente.', 401);
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message = errorBody.message || `Erro HTTP ${response.status}: ${response.statusText}`;
    throw new ApiError(message, response.status);
  }

  return response.json();
}

export const api = {
  // Auth
  getMe: () => request<{ user: any; customerId: string }>('/auth/me'),

  // Dashboard
  getClientDashboard: () => request<any>('/client/dashboard'),

  // Projects
  getProjects: () => request<{ items: any[]; total: number }>('/projects'),
  getProject: (id: string) => request<any>(`/projects/${id}`),
  createProject: (data: any) => request<any>('/projects', { method: 'POST', body: JSON.stringify(data) }),
  updateProject: (id: string, data: any) => request<any>(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteProject: (id: string) => request<any>(`/projects/${id}`, { method: 'DELETE' }),

  // Lessons
  getLessons: () => request<{ items: Lesson[]; total: number }>('/lessons'),
  getProjectLessons: (projectId: string) => request<{ items: Lesson[]; total: number }>(`/projects/${projectId}/lessons`),
  getLesson: (id: string) => request<Lesson>(`/lessons/${id}`),
  createLesson: (projectId: string, data: any) =>
    request<Lesson>(`/projects/${projectId}/lessons`, { method: 'POST', body: JSON.stringify(data) }),
  updateLesson: (id: string, data: any) => request<Lesson>(`/lessons/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteLesson: (id: string) => request<any>(`/lessons/${id}`, { method: 'DELETE' }),

  // v3.0 Import & Lesson Configs
  importLesson: (formData: FormData) =>
    request<{ success: boolean; message: string; lesson: Lesson; scenes: Scene[]; warnings?: string[] }>('/lessons/import', {
      method: 'POST',
      body: formData,
    }),
  analyzeBatchImport: (projectId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return request<BatchImportAnalyzeResult>(`/projects/${projectId}/import-batch/analyze`, {
      method: 'POST',
      body: formData,
    });
  },
  confirmBatchImport: (projectId: string, payload: BatchImportConfirmPayload) =>
    request<{ success: boolean; createdLessonCount: number; lessons: Lesson[] }>(
      `/projects/${projectId}/import-batch/confirm`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    ),
  updateLessonDefaults: (lessonId: string, defaults: Partial<LessonSceneDefaults>) =>
    request<Lesson>(`/lessons/${lessonId}/defaults`, { method: 'PATCH', body: JSON.stringify(defaults) }),
  applyLessonDefaults: (lessonId: string, overwriteCustom: boolean) =>
    request<{ affectedScenes: number; message: string }>(`/lessons/${lessonId}/apply-defaults`, {
      method: 'POST',
      body: JSON.stringify({ overwriteCustom }),
    }),
  reorderScenes: (lessonId: string, orderedSceneIds: string[]) =>
    request<{ success: boolean; items: Scene[]; total: number }>(`/lessons/${lessonId}/reorder-scenes`, {
      method: 'POST',
      body: JSON.stringify({ orderedSceneIds }),
    }),
  getLessonPreview: (lessonId: string) =>
    request<{
      lesson: Lesson;
      scenes: (Scene & { effectiveSettings: LessonSceneDefaults; isCustomized: boolean })[];
      renderedVideo: any;
      hasNewerContentThanRender: boolean;
      totalScenes: number;
      warningCount: number;
    }>(`/lessons/${lessonId}/preview`),

  // Scenes
  getScenes: (lessonId: string) => request<{ items: Scene[]; total: number }>(`/lessons/${lessonId}/scenes`),
  getScene: (id: string) => request<Scene>(`/scenes/${id}`),
  createScene: (lessonId: string, data: any) =>
    request<Scene>(`/lessons/${lessonId}/scenes`, { method: 'POST', body: JSON.stringify(data) }),
  updateScene: (id: string, data: any) => request<Scene>(`/scenes/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  audioPreviewScene: (id: string) =>
    request<{
      sceneId: string;
      narrationAudioUrl: string;
      narrationDurationSeconds: number;
      transitionAudioUrl?: string;
      transitionDurationSeconds?: number;
      totalDurationSeconds: number;
      effectiveSettings: LessonSceneDefaults;
    }>(`/scenes/${id}/audio-preview`, { method: 'POST' }),
  deleteScene: (id: string) => request<any>(`/scenes/${id}`, { method: 'DELETE' }),

  // Quota
  getQuota: () => request<any>('/quota/current'),
  getQuotaHistory: () => request<{ items: any[] }>('/quota/history'),

  // Render Jobs
  renderLesson: (lessonId: string, options?: { voiceId?: string; renderProfile?: string }) =>
    request<any>(`/render/lessons/${lessonId}`, { method: 'POST', body: JSON.stringify(options || {}) }),
  renderBatchLessons: (lessonIds: string[]) =>
    request<{ success: boolean; queuedCount: number; jobs: any[]; errors: any[] }>('/render/batch', {
      method: 'POST',
      body: JSON.stringify({ lessonIds }),
    }),
  getJobs: (status?: string) => request<{ items: any[]; total: number }>(`/render/jobs${status ? `?status=${status}` : ''}`),
  getJob: (id: string) => request<any>(`/render/jobs/${id}`),
  cancelJob: (id: string) => request<any>(`/render/jobs/${id}/cancel`, { method: 'POST' }),

  // Videos
  getVideos: () => request<{ items: any[]; total: number }>('/videos'),
  getVideo: (id: string) => request<any>(`/videos/${id}`),

  // Uploads
  initUpload: (data: any) => request<any>('/uploads/init', { method: 'POST', body: JSON.stringify(data) }),
  confirmUpload: (id: string) => request<any>(`/uploads/${id}/confirm`, { method: 'POST' }),

  // Google Drive Integration V2.0
  getGoogleDriveIntegration: () => request<import('../types/index.js').GoogleDriveConnection>('/integrations/google-drive'),
  connectGoogleDrive: (redirectUrl?: string) =>
    request<{ authUrl: string }>('/integrations/google-drive/connect', {
      method: 'POST',
      body: JSON.stringify({ redirectUrl }),
    }),
  testGoogleDriveConnection: () =>
    request<{ success: boolean; message: string }>('/integrations/google-drive/test', {
      method: 'POST',
    }),
  updateGoogleDriveSettings: (settings: Partial<import('../types/index.js').GoogleDriveConnectionSettings>) =>
    request<import('../types/index.js').GoogleDriveConnection>('/integrations/google-drive/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    }),
  disconnectGoogleDrive: () =>
    request<{ success: boolean; message: string }>('/integrations/google-drive/disconnect', {
      method: 'POST',
    }),

  // Google Drive Transfers & Queue
  createTransferBatch: (projectIds: string[]) =>
    request<{
      batch: import('../types/index.js').TransferBatch;
      jobs: import('../types/index.js').TransferJob[];
      summary: any;
    }>('/google-drive/transfers', {
      method: 'POST',
      body: JSON.stringify({ projectIds }),
    }),
  getTransferBatches: (status?: string, limit?: number) =>
    request<{ items: import('../types/index.js').TransferBatch[]; total: number }>(
      `/google-drive/transfers?${status ? `status=${status}&` : ''}${limit ? `limit=${limit}` : ''}`
    ),
  getTransferBatch: (id: string) =>
    request<{
      batch: import('../types/index.js').TransferBatch;
      jobs: import('../types/index.js').TransferJob[];
    }>(`/google-drive/transfers/${id}`),
  cancelTransferBatch: (id: string) =>
    request<{ success: boolean; message: string }>(`/google-drive/transfers/${id}/cancel`, {
      method: 'POST',
    }),
  retryTransfer: (id: string) =>
    request<{ success: boolean; job?: any; message?: string }>(`/google-drive/transfers/${id}/retry`, {
      method: 'POST',
    }),

  // YouTube Integration V1.0
  getYouTubeIntegration: () => request<import('../types/index.js').YouTubeConnection>('/integrations/youtube'),
  connectYouTube: (redirectUrl?: string) =>
    request<{ authUrl: string }>('/integrations/youtube/connect', {
      method: 'POST',
      body: JSON.stringify({ redirectUrl }),
    }),
  testYouTubeConnection: () =>
    request<{ success: boolean; message: string; channel?: any }>('/integrations/youtube/test', {
      method: 'POST',
    }),
  updateYouTubeSettings: (settings: Partial<import('../types/index.js').YouTubeTransferSettings>) =>
    request<import('../types/index.js').YouTubeTransferSettings>('/integrations/youtube/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    }),
  disconnectYouTube: () =>
    request<{ success: boolean; message: string }>('/integrations/youtube/disconnect', {
      method: 'POST',
    }),
  getYouTubeChannel: () =>
    request<{ channelId: string; channelTitle: string; channelThumbnail?: string; channelCustomUrl?: string; status: string }>('/youtube/channel'),

  // YouTube Transfers & Queue
  createYouTubeTransferBatch: (projectIds: string[], audienceSettings?: Record<string, boolean>) =>
    request<{
      batch: import('../types/index.js').TransferBatch;
      jobs: import('../types/index.js').TransferJob[];
      summary: any;
    }>('/youtube/transfers', {
      method: 'POST',
      body: JSON.stringify({ projectIds, audienceSettings }),
    }),
  getYouTubeTransferBatches: (status?: string, limit?: number) =>
    request<{ items: import('../types/index.js').TransferBatch[]; total: number }>(
      `/youtube/transfers?${status ? `status=${status}&` : ''}${limit ? `limit=${limit}` : ''}`
    ),
  getYouTubeTransferBatch: (id: string) =>
    request<{
      batch: import('../types/index.js').TransferBatch;
      jobs: import('../types/index.js').TransferJob[];
    }>(`/youtube/transfers/${id}`),
  cancelYouTubeTransferBatch: (id: string) =>
    request<{ success: boolean; message: string }>(`/youtube/transfers/${id}/cancel`, {
      method: 'POST',
    }),
  retryYouTubeTransfer: (id: string) =>
    request<{ success: boolean; job?: any; message?: string }>(`/youtube/transfers/${id}/retry`, {
      method: 'POST',
    }),

  // Downloads (Single & Batch ZIP)
  downloadLessonMp4: async (lessonId: string, defaultName?: string) => {
    let token = localStorage.getItem('edurender_token') || 'dev-token-customer-admin';
    if (auth.currentUser) {
      try {
        token = await auth.currentUser.getIdToken();
      } catch {}
    }
    const res = await fetch(`${API_BASE}/lessons/${lessonId}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Falha no download da videoaula.' }));
      throw new Error(err.message || 'Falha no download da videoaula.');
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = defaultName || `Aula_${lessonId}.mp4`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },

  createBatchDownload: (lessonIds: string[], projectId?: string) =>
    request<import('../types/index.js').DownloadJob>('/downloads/batch', {
      method: 'POST',
      body: JSON.stringify({ lessonIds, projectId }),
    }),

  createProjectDownload: (projectId: string) =>
    request<{
      job: import('../types/index.js').DownloadJob;
      summary: any;
    }>(`/projects/${projectId}/download`, {
      method: 'POST',
    }),

  getDownloadJob: (id: string) =>
    request<import('../types/index.js').DownloadJob>(`/downloads/${id}`),

  getDownloadJobFileUrl: (id: string) => `${API_BASE}/downloads/${id}/file`,
};

